import type { Table } from 'dexie';

import { getDb } from '../../core/db';
import { newId } from '../../core/ids';
import type { AutomationEvent, AutomationEventKind } from '../../core/types';
import { getMomentAccountId } from '../moments/accountScope';

function table(): Table<AutomationEvent, string> {
  return getDb().automationEvents;
}

export interface ScheduleAutomationInput {
  presetId: string;
  kind: AutomationEventKind;
  dueAt: number;
  sourceMessageId?: string;
  now?: number;
}

export async function scheduleAutomation(input: ScheduleAutomationInput): Promise<AutomationEvent> {
  const now = input.now ?? Date.now();
  return getDb().transaction('rw', getDb().automationEvents, async () => {
    if (input.kind !== 'bubble' && input.sourceMessageId) {
      const previous = await table().where('presetId').equals(input.presetId)
        .filter(event => event.kind === input.kind && event.sourceMessageId === input.sourceMessageId
          && (event.userId === undefined || event.userId === getMomentAccountId())).first();
      if (previous) return previous;
    }
    const existing = await table()
      .where('presetId')
      .equals(input.presetId)
      .filter((event) => event.kind === input.kind && event.status === 'queued')
      .toArray();
    if (existing.length > 0) {
      if (input.kind !== 'bubble') return existing.sort((a, b) => a.dueAt - b.dueAt)[0]!;
      await table().bulkDelete(existing.map((event) => event.id));
    }
    const event: AutomationEvent = {
      id: newId(),
      presetId: input.presetId,
      kind: input.kind,
      dueAt: input.dueAt,
      status: 'queued',
      createdAt: now,
      updatedAt: now,
      ...(input.sourceMessageId ? { sourceMessageId: input.sourceMessageId } : {}),
      ...(input.kind !== 'bubble' && getMomentAccountId() ? { userId: getMomentAccountId()! } : {}),
    };
    await table().put(event);
    return event;
  });
}

export async function listAutomationEvents(): Promise<AutomationEvent[]> {
  return table().toArray();
}

/** Claim a due event atomically so two open tabs cannot execute it twice. */
export async function claimDueAutomation(
  now = Date.now(),
  staleBefore = now - 10 * 60_000,
): Promise<AutomationEvent | null> {
  return getDb().transaction('rw', getDb().automationEvents, async () => {
    const rows = await table().toArray();
    const due = rows
      .filter((event) =>
        event.kind === 'bubble' && ((event.status === 'queued' && event.dueAt <= now) ||
        (event.status === 'running' && event.updatedAt < staleBefore)),
      )
      .sort((a, b) => a.dueAt - b.dueAt || (a.id < b.id ? -1 : 1));
    const event = due[0];
    if (event === undefined) return null;
    const claimed: AutomationEvent = {
      ...event,
      status: 'running',
      updatedAt: now,
      error: undefined,
    };
    await table().put(claimed);
    return claimed;
  });
}

export async function finishAutomation(
  id: string,
  status: AutomationEvent['status'],
  error?: string,
  now = Date.now(),
): Promise<void> {
  const existing = await table().get(id);
  if (existing === undefined) return;
  await table().put({
    ...existing,
    status,
    updatedAt: now,
    ...(error ? { error } : { error: undefined }),
  });
}

export async function rescheduleAutomation(id: string, dueAt: number, now = Date.now()): Promise<void> {
  const existing = await table().get(id);
  if (existing === undefined) return;
  await table().put({ ...existing, status: 'queued', dueAt, updatedAt: now, error: undefined });
}

export async function cancelQueuedAutomation(presetId: string, kind?: AutomationEventKind): Promise<number> {
  const rows = await table()
    .where('presetId')
    .equals(presetId)
    .filter((event) => event.status === 'queued' && (kind === undefined || event.kind === kind))
    .toArray();
  if (rows.length === 0) return 0;
  await table().bulkDelete(rows.map((event) => event.id));
  return rows.length;
}

export async function clearAutomationEvents(): Promise<void> {
  await table().clear();
}
